import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { SharedModule } from '@shared/shared.module';
import { FloatingPanelService } from './floating-panel.service';
import { FloatingPanelDockZone, FloatingPanelRef } from './floating-panel.types';
import { FLOATING_PANEL_DATA } from './floating-panel-data.token';
import { DOCK_RENDER_ZONE } from './dock-render-zone.token';
import { FLOATING_PANEL_REF } from './floating-panel-ref.token';
import { DockFixedTabDirective } from './dock-fixed-tab.directive';
import { floatingPanelLockTooltip, floatingPanelUnlockTooltip } from './floating-panel-lock.texts';
import { ViewportService } from '@core/services/viewport.service';

/** Scheidt in de scrollgeheugen-key de tab-key van de content-key van het paneel. */
const CONTENT_MEMORY_SEPARATOR = '\u0000';

/**
 * Markeer hiermee een element in gedockte inhoud dat zelf horizontaal scrolt en
 * baat heeft bij een breder dock (bv. een tabelwrapper met `overflow-x: auto`):
 * zolang het overloopt, meldt de dock {@link DockAreaComponent.wantsMoreWidth}.
 * Overloop van de dock-scroller zelf telt automatisch; deze markering is enkel
 * nodig voor inhoud die binnenin scrolt. Niet voor tijdlijnen die by design
 * pannen (die lopen altijd over).
 */
export const DOCK_WIDE_CONTENT_ATTRIBUTE = 'data-dock-wide-content';

interface DockScrollContainerPosition {
  readonly element: HTMLElement;
  readonly top: number;
  readonly left: number;
}

interface ActivateTabOptions {
  readonly rememberCurrentScroll?: boolean;
}

interface ScheduledScrollRestore {
  readonly frameId: number;
  readonly view: Window;
}

/**
 * Uitgebreide dock-host die — anders dan {@link DockedPanelsHostComponent} —
 * meerdere **vaste** tabs (via {@link DockFixedTabDirective}) ondersteunt en een
 * configureerbare **dock-zone-capability** registreert. Bedoeld voor de BAC-
 * pagina-layout met een dubbel-brede rechter-area die zowel de `'small'`- als
 * de `'full'`-zone kan tonen.
 *
 * Gedrag:
 *  - **Tab-strip**: één knop per vaste tab + één chip per gedockt paneel
 *    (waarvan de effectieve dock-zone in {@link zones} valt).
 *  - **Side-by-side**: zolang een vaste tab actief is en {@link sideBySideFixedTabs}
 *    `true` is, worden álle vaste tabs naast elkaar getoond (de twee
 *    configuratoren). Zodra een paneel-tab (bv. een `'full'` gate-info dialog)
 *    geactiveerd wordt, neemt dat paneel de volledige breedte in en verbergen
 *    de configuratoren (ze blijven gemount → state behouden).
 *  - Registreert zichzelf als dock-target met zijn zone-capability zodat de
 *    service `'full'`-panels enkel hier honoreert.
 */
@Component({
  selector: 'dock-area',
  templateUrl: './dock-area.component.html',
  styleUrl: './dock-area.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, NgTemplateOutlet, SharedModule],
  host: {
    '[class.is-drop-target]': 'isDropTarget()',
  },
})
export class DockAreaComponent implements OnInit, OnDestroy {
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly parentInjector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  /** Phone: de omliggende scroller is de paginascroller — die nooit verzetten. */
  private readonly viewport = inject(ViewportService);

  /** Dock-zone-groottes die deze host kan tonen (default beide). */
  readonly zones = input<readonly FloatingPanelDockZone[]>(['small', 'full']);
  /** Toon alle vaste tabs naast elkaar zolang geen paneel-tab actief is. */
  readonly sideBySideFixedTabs = input(true);
  /**
   * De host is ingeklapt: hij blijft gemount (gedockte panelen houden hun
   * staat) maar is onzichtbaar, dus mag de auto-dock-heuristiek er geen nieuwe
   * panelen naartoe sturen. Zie `FloatingPanelService.shouldAutoDock`.
   */
  readonly collapsed = input(false);

  protected readonly fixedTabs = contentChildren(DockFixedTabDirective);
  private readonly contentElement = viewChild<ElementRef<HTMLElement>>('content');

  /** Enkel de gedockte panels waarvan de effectieve zone in `zones` valt. */
  protected readonly dockedPanels = computed<readonly FloatingPanelRef<unknown, unknown>[]>(() => {
    const zoneSet = new Set(this.zones());
    return this.floatingPanels.dockedPanels().filter(panel => zoneSet.has(panel.dockZone()));
  });

  protected readonly activeTabKey = signal<string>('');
  protected readonly isDropTarget = this.floatingPanels.isDragOverDockZone();
  protected readonly justAutoDockedKey = this.floatingPanels.getJustAutoDockedKey();

  protected readonly isFixedActive = computed(() => {
    const key = this.activeTabKey();
    return key === '' || key.startsWith('fixed:');
  });

  private readonly injectorCache = new Map<string, Injector>();
  private unregisterDockTarget: (() => void) | null = null;
  private previousDockedKeys: readonly string[] = [];
  private selectedTabHistory: readonly string[] = [];
  /** Per tab, en voor een paneel met content-key per inhoud (zie {@link scrollMemoryKey}). */
  private readonly tabScrollPositions = new Map<string, readonly DockScrollContainerPosition[]>();
  private pendingScrollRestore: ScheduledScrollRestore | null = null;
  private pulseClearTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Laatst gerenderde inhoud van de actieve tab (zie de content-key-effect). */
  private activeContent: { readonly tabKey: string; readonly contentKey: string | null } | null = null;

  protected readonly lockTooltip = floatingPanelLockTooltip;
  protected readonly unlockTooltip = floatingPanelUnlockTooltip;

  private readonly wantsMoreWidthState = signal(false);
  /**
   * De zichtbare inhoud is breder dan de dock: de dock-scroller zelf loopt
   * horizontaal over, of een element met {@link DOCK_WIDE_CONTENT_ATTRIBUTE}
   * doet dat. De pagina toont dan de sleeplijn om de dock te verbreden.
   */
  readonly wantsMoreWidth = this.wantsMoreWidthState.asReadonly();

  constructor() {
    // Default-activatie van de eerste vaste tab zodra de tabs beschikbaar zijn.
    effect(() => {
      const fixed = this.fixedTabs();
      if (this.activeTabKey() === '' && fixed.length > 0) {
        this.activateTab(this.fixedKey(0));
      }
    });

    // Scrollpositie van de actieve tab bijhouden (herstel bij terugkeer).
    // BELANGRIJK — buiten de Angular-zone, niet als `(scroll)`-binding: die gaf
    // één volledige change detection per scroll-event (bij wielen ~60/s, elk
    // ~2 ms over de hele app; gemeten 2026-10-13), terwijl de handler enkel een
    // positie in een Map noteert. Zie dock-area.component.spec.ts
    // ("listener buiten de zone").
    effect(onCleanup => {
      const content = this.contentElement()?.nativeElement;
      if (!content) return;
      const remember = () => this.rememberActiveScrollPosition();
      untracked(() => this.zone.runOutsideAngular(() => content.addEventListener('scroll', remember, { passive: true })));
      onCleanup(() => content.removeEventListener('scroll', remember));
    });

    effect(() => {
      const docked = this.dockedPanels();
      const currentKeys = docked.map(panel => panel.key);
      const availableTabKeys = this.availableTabKeys(docked);
      const newlyAddedKey = currentKeys.find(key => !this.previousDockedKeys.includes(key));
      this.pruneSelectedTabHistory(availableTabKeys);
      this.pruneTabScrollPositions(availableTabKeys);

      if (newlyAddedKey) {
        this.activateTab(newlyAddedKey);
      }

      // Terug naar de meest recent gebruikte tab wanneer de actieve tab verdwijnt.
      const active = this.activeTabKey();
      if (active !== '' && !availableTabKeys.includes(active)) {
        this.activateMostRecentAvailableTab(availableTabKeys);
      }

      for (const cachedKey of [...this.injectorCache.keys()]) {
        if (!docked.some(panel => panel.key === cachedKey)) {
          this.injectorCache.delete(cachedKey);
        }
      }

      this.previousDockedKeys = currentKeys;
    });

    // Leesplek per inhoud: wisselt de inhoud van de actieve paneel-tab (een
    // paneel dat de actieve chart volgt, na een chart-tabwissel), dan zet de
    // dock de positie van die inhoud terug: terug naar dezelfde inhoud = terug
    // op dezelfde plek, nieuwe inhoud begint bovenaan. De positie van de vorige
    // inhoud staat al genoteerd door de scroll-listener (onder zijn eigen key).
    effect(() => {
      const tabKey = this.activeTabKey();
      const contentKey = this.dockedPanels().find(panel => panel.key === tabKey)?.contentKey() ?? null;
      untracked(() => {
        const previous = this.activeContent;
        this.activeContent = { tabKey, contentKey };
        if (previous?.tabKey === tabKey && previous.contentKey !== contentKey) {
          this.restoreScrollPosition(tabKey);
        }
      });
    });

    effect(() => {
      const key = this.justAutoDockedKey();
      if (this.pulseClearTimeout !== null) {
        clearTimeout(this.pulseClearTimeout);
        this.pulseClearTimeout = null;
      }
      if (key === null) return;
      // Een paneel dat aandacht vraagt (nieuw auto-gedockt óf een her-klik op een
      // reeds gedockte tab in "full docked"-modus) wordt meteen de actieve tab,
      // zodat de gebruiker de inhoud effectief ziet i.p.v. enkel een pulse.
      if (untracked(() => this.dockedPanels()).some(panel => panel.key === key)) {
        this.activateTab(key);
      }
      this.pulseClearTimeout = setTimeout(() => {
        this.floatingPanels.clearJustAutoDocked();
        this.pulseClearTimeout = null;
      }, 900);
    });

    // Breedtevraag (zie wantsMoreWidth), buiten de zone en hooguit één meting
    // per frame. Opnieuw meten wanneer de dock zelf van maat wisselt (slepen,
    // venster) of zijn inhoud wijzigt: een tabwissel ([hidden]), een nieuw
    // paneel, een tabel die later rendert. De panes zelf meten niet mee: ze
    // zijn altijd zo hoog als de scroller, ook als hun inhoud groeit.
    effect(onCleanup => {
      const content = this.contentElement()?.nativeElement;
      const view = content?.ownerDocument.defaultView;
      if (!content || !view) return;
      let frameId: number | null = null;
      const scheduleMeasure = (): void => {
        frameId ??= view.requestAnimationFrame(() => {
          frameId = null;
          this.measureWidthDemand(content);
        });
      };
      const { resizeObserver, mutationObserver } = untracked(() => this.zone.runOutsideAngular(() => ({
        resizeObserver: new ResizeObserver(scheduleMeasure),
        mutationObserver: new MutationObserver(scheduleMeasure),
      })));
      resizeObserver.observe(content);
      mutationObserver.observe(content, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden'],
      });
      onCleanup(() => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        if (frameId !== null) view.cancelAnimationFrame(frameId);
      });
    });

    this.destroyRef.onDestroy(() => {
      if (this.pulseClearTimeout !== null) {
        clearTimeout(this.pulseClearTimeout);
        this.pulseClearTimeout = null;
      }
      this.cancelPendingScrollRestore();
    });
  }

  ngOnInit(): void {
    this.unregisterDockTarget = this.floatingPanels.registerDockTarget(
      this.elementRef.nativeElement,
      { zones: this.zones(), hidden: this.collapsed },
    );
  }

  ngOnDestroy(): void {
    this.unregisterDockTarget?.();
    this.unregisterDockTarget = null;
  }

  protected fixedKey(index: number): string {
    return `fixed:${index}`;
  }

  protected setActive(tabKey: string): void {
    this.activateTab(tabKey);
  }

  /**
   * Activeer programmatisch een vaste tab op zijn id (`dockFixedTabId`) of,
   * zonder id, op zijn label (bv. "advanced" bij het openen van een cycle
   * chart). Gebruik bij vertaalde labels altijd de id: het label wisselt mee
   * met de taal. Geeft `false` terug wanneer de tab (nog) niet bestaat, zodat
   * de caller het later opnieuw kan proberen.
   */
  activateFixedTab(key: string): boolean {
    const index = this.fixedTabs().findIndex(tab => (tab.id() ?? tab.label()) === key);
    if (index === -1) {
      return false;
    }
    this.activateTab(this.fixedKey(index));
    return true;
  }

  protected rememberActiveScrollPosition(): void {
    this.rememberScrollPosition(this.activeTabKey());
  }

  protected handleUndock(event: MouseEvent, panel: FloatingPanelRef<unknown, unknown>): void {
    event.stopPropagation();
    this.rememberActiveScrollPosition();
    panel.undock();
  }

  protected handleClose(event: MouseEvent, panel: FloatingPanelRef<unknown, unknown>): void {
    event.stopPropagation();
    this.rememberActiveScrollPosition();
    panel.close();
  }

  protected handleToggleLock(event: MouseEvent, panel: FloatingPanelRef<unknown, unknown>): void {
    event.stopPropagation();
    panel.toggleLock();
  }

  protected injectorForPanel(panel: FloatingPanelRef<unknown, unknown>): Injector {
    const cached = this.injectorCache.get(panel.key);
    if (cached) return cached;
    const created = Injector.create({
      parent: this.parentInjector,
      providers: [
        { provide: FLOATING_PANEL_DATA, useValue: panel.data },
        { provide: DOCK_RENDER_ZONE, useValue: panel.dockZone() },
        { provide: FLOATING_PANEL_REF, useValue: panel },
      ],
    });
    this.injectorCache.set(panel.key, created);
    return created;
  }

  private availableTabKeys(dockedPanels: readonly FloatingPanelRef<unknown, unknown>[]): readonly string[] {
    const fixedTabs = this.fixedTabs();
    const fixedTabKeys: string[] = [];
    for (let fixedTabIndex = 0; fixedTabIndex < fixedTabs.length; fixedTabIndex += 1) {
      fixedTabKeys.push(this.fixedKey(fixedTabIndex));
    }
    return [...fixedTabKeys, ...dockedPanels.map(panel => panel.key)];
  }

  private activateTab(tabKey: string, options: ActivateTabOptions = {}): void {
    const previousTabKey = this.activeTabKey();
    const rememberCurrentScroll = options.rememberCurrentScroll ?? true;
    if (rememberCurrentScroll && previousTabKey !== tabKey) {
      this.rememberScrollPosition(previousTabKey);
    }
    this.activeTabKey.set(tabKey);
    this.rememberSelectedTab(tabKey);
    this.restoreScrollPosition(tabKey);
  }

  private rememberSelectedTab(tabKey: string): void {
    if (tabKey === '') return;
    this.selectedTabHistory = [
      tabKey,
      ...this.selectedTabHistory.filter(selectedTabKey => selectedTabKey !== tabKey),
    ];
  }

  private pruneSelectedTabHistory(availableTabKeys: readonly string[]): void {
    const availableTabKeySet = new Set(availableTabKeys);
    this.selectedTabHistory = this.selectedTabHistory.filter(tabKey => availableTabKeySet.has(tabKey));
  }

  private pruneTabScrollPositions(availableTabKeys: readonly string[]): void {
    const availableTabKeySet = new Set(availableTabKeys);
    for (const memoryKey of this.tabScrollPositions.keys()) {
      if (!availableTabKeySet.has(memoryKey.split(CONTENT_MEMORY_SEPARATOR)[0])) {
        this.tabScrollPositions.delete(memoryKey);
      }
    }
  }

  /**
   * Key in {@link tabScrollPositions}: de tab-key, voor een paneel met
   * content-key aangevuld met de getoonde inhoud. Untracked: wordt ook vanuit
   * effects aangeroepen (tab activeren), die anders van elke content-key
   * zouden gaan afhangen.
   */
  private scrollMemoryKey(tabKey: string): string {
    const contentKey = untracked(() => this.dockedPanels().find(panel => panel.key === tabKey)?.contentKey() ?? null);
    return contentKey === null ? tabKey : `${tabKey}${CONTENT_MEMORY_SEPARATOR}${contentKey}`;
  }

  private activateMostRecentAvailableTab(availableTabKeys: readonly string[]): void {
    const nextTabKey = this.selectedTabHistory.find(tabKey => availableTabKeys.includes(tabKey))
      ?? availableTabKeys[0]
      ?? '';
    this.activateTab(nextTabKey, { rememberCurrentScroll: false });
  }

  private rememberScrollPosition(tabKey: string): void {
    if (tabKey === '') return;
    const scrollContainers = this.scrollContainers();
    if (scrollContainers.length === 0) return;
    this.tabScrollPositions.set(
      this.scrollMemoryKey(tabKey),
      scrollContainers.map(element => ({
        element,
        top: element.scrollTop,
        left: element.scrollLeft,
      })),
    );
  }

  private restoreScrollPosition(tabKey: string): void {
    const targetPositions = this.restorePositionsFor(tabKey);
    if (targetPositions.length === 0) return;
    this.applyScrollPositions(targetPositions);
    this.scheduleScrollPositionRestore(tabKey, targetPositions);
  }

  private scheduleScrollPositionRestore(tabKey: string, positions: readonly DockScrollContainerPosition[]): void {
    const content = this.contentElement()?.nativeElement;
    const view = content?.ownerDocument.defaultView;
    if (!view) return;

    this.cancelPendingScrollRestore();
    const frameId = view.requestAnimationFrame(() => {
      this.pendingScrollRestore = null;
      if (this.activeTabKey() !== tabKey) return;
      this.applyScrollPositions(positions);
    });
    this.pendingScrollRestore = { frameId, view };
  }

  private cancelPendingScrollRestore(): void {
    if (this.pendingScrollRestore === null) return;
    this.pendingScrollRestore.view.cancelAnimationFrame(this.pendingScrollRestore.frameId);
    this.pendingScrollRestore = null;
  }

  private applyScrollPositions(positions: readonly DockScrollContainerPosition[]): void {
    for (const position of positions) {
      position.element.scrollTop = position.top;
      position.element.scrollLeft = position.left;
    }
  }

  private restorePositionsFor(tabKey: string): readonly DockScrollContainerPosition[] {
    const scrollContainers = this.scrollContainers();
    if (scrollContainers.length === 0) return [];
    const content = this.contentElement()?.nativeElement ?? null;
    const savedPositions = this.tabScrollPositions.get(this.scrollMemoryKey(tabKey)) ?? [];
    const savedPositionsByElement = new Map(savedPositions.map(position => [position.element, position]));
    return scrollContainers.flatMap(element => {
      const savedPosition = savedPositionsByElement.get(element);
      // Gedeelde ancestor-scrollers alleen aanraken waar dat geen springen
      // veroorzaakt. Op phone is de ancestor de paginascroller (dock ónder de
      // chart): daar nooit iets terugzetten — de gebruiker blijft staan waar
      // hij bezig is (finetune 2026-09-15). Zonder opgeslagen positie mag een
      // ancestor enkel naar 0 wanneer hij de facto de dock-scroller is (de
      // kaart-body waarin de dock bovenaan begint) — anders zou een nieuw
      // geopend paneel op de scrollpositie van de vorige tab starten
      // (bugfix 2026-09-22); ancestors met pagina-content bóven het dock
      // blijven met rust (de oorspronkelijke 2026-09-15-klacht).
      if (element !== content) {
        if (this.viewport.isPhone()) {
          return [];
        }
        if (savedPosition == null && !this.isDockLocalScroller(element)) {
          return [];
        }
      }
      return [{
        element,
        top: savedPosition?.top ?? 0,
        left: savedPosition?.left ?? 0,
      }];
    });
  }

  /**
   * Is deze ancestor-scroller de facto de dock-scroller? Dat is zo wanneer de
   * dock-host er praktisch bovenaan begint (alleen kaart-padding of een kop
   * erboven) én de scroller niet wezenlijk breder is dan de dock (dan scrolt
   * hij geen pagina-kolommen naast de dock mee). Voor zo'n scroller is een
   * 0-reset bij een verse tab veilig en gewenst.
   */
  private isDockLocalScroller(ancestor: HTMLElement): boolean {
    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const ancestorRect = ancestor.getBoundingClientRect();
    const contentTopOffset = hostRect.top - ancestorRect.top + ancestor.scrollTop;
    const widthRatio = ancestor.clientWidth / Math.max(hostRect.width, 1);
    return contentTopOffset <= 160 && widthRatio <= 1.5;
  }

  private scrollContainers(): readonly HTMLElement[] {
    const content = this.contentElement()?.nativeElement;
    if (!content) return [];
    const containers = [content];
    let ancestor = this.elementRef.nativeElement.parentElement;
    while (ancestor && ancestor !== content.ownerDocument.body && ancestor !== content.ownerDocument.documentElement) {
      if (this.isScrollableContainer(ancestor)) {
        containers.push(ancestor);
      }
      ancestor = ancestor.parentElement;
    }
    return containers;
  }

  private isScrollableContainer(element: HTMLElement): boolean {
    return element.scrollTop > 0
      || element.scrollLeft > 0
      || element.scrollHeight > element.clientHeight
      || element.scrollWidth > element.clientWidth;
  }

  /**
   * Hoeveel px de dock nu breder moet om zijn zichtbare inhoud zonder horizontale
   * scrollbalk te tonen (0 = past): de grootste overloop van de dock-scroller en
   * de elementen met {@link DOCK_WIDE_CONTENT_ATTRIBUTE}. Een meting op aanvraag
   * (het pas-aan-de-inhoud-knopje van de sleeplijn), geen signal: dat getal wisselt
   * tijdens het slepen elk frame en zou dan telkens een change detection geven.
   */
  missingWidth(): number {
    const content = this.contentElement()?.nativeElement;
    return content ? missingWidthOf(content) : 0;
  }

  private measureWidthDemand(content: HTMLElement): void {
    const wantsMoreWidth = missingWidthOf(content) > 0;
    if (wantsMoreWidth === this.wantsMoreWidthState()) return;
    this.zone.run(() => this.wantsMoreWidthState.set(wantsMoreWidth));
  }
}

/** Verborgen panes (`[hidden]`) meten 0 breed en tellen dus niet mee. */
function missingWidthOf(content: HTMLElement): number {
  const wideContent = content.querySelectorAll<HTMLElement>(`[${DOCK_WIDE_CONTENT_ATTRIBUTE}]`);
  return Math.max(horizontalOverflow(content), ...Array.from(wideContent, horizontalOverflow));
}

/** 1px speling: subpixel-afronding is geen overloop. */
function horizontalOverflow(element: HTMLElement): number {
  const overflow = element.scrollWidth - element.clientWidth;
  return overflow > 1 ? overflow : 0;
}
